import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";
import endpoints from "../../api/endpoints";
import en from "../../locales/en.json";
import { ComparedExposer, ShopLiftResponse, BrandInsightsResponse, StoreLocation, WeeklyReachAnalysisResponse, SmartRequestListResponse, VisitedShopListResponse, ShopEffectResponse, ReceiptAnalysisResponse } from "./types";

export const fetchCountryList = async () => {
  try {
    const response = await axiosInstance.get(endpoints.publicApis.countryList);
    if (response?.data?.result?.length > 0) {
      return response.data.result;
    }
    return [];
  } catch {
    return [];
  }
};

export const fetchLanguageList = async () => {
  try {
    const response = await axiosInstance.get(endpoints.publicApis.languageList);
    if (response?.data?.result?.length > 0) {
      return response.data.result;
    }
    return [];
  } catch {
    return [];
  }
};

export const fetchDashBoardSummary = async (data: any) => {
  try {
    const response = await axiosInstance.get(endpoints.dashboard.summary(data));
    if (response?.data?.result) {
      return response.data.result;
    }
    return null;
  } catch {
    return null;
  }
};

export const fetchCountryWiseActiveUsers = async () => {
  try {
    const response = await axiosInstance.get(
      endpoints.dashboard.countryWiseActiveUsers
    );
    if (response?.data?.result) {
      return response.data.result;
    }
    return null;
  } catch {
    return null;
  }
};

export const fetchComparedExposureData = async (
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setExposureData: React.Dispatch<
    React.SetStateAction<ComparedExposer | undefined>
  >,
  params: any
) => {
  try {
    setLoading(true);
    const url = endpoints.dashboard.fetchExposerData(params);
    const response = await axiosInstance.get(url);
    if (response?.data) {
      setExposureData(response.data);
    } else {
      setExposureData(undefined);
    }
    setLoading(false);
  } catch (error: any) {
    setExposureData(undefined);
    setLoading(false);
    toast.error(
        `${en.common.failedToFetchData}`
    );
  }
};

// Shop Lift API
export const fetchShopLiftData = async (params: any) => {
  try {
    const url = endpoints.dashboard.shopLift();
    const response = await axiosInstance.post<ShopLiftResponse>(url, params);
    
    if (response?.data?.success && response?.data?.result) {
      return { success: true, result: response.data.result };
    } else {
      toast.error("Failed to fetch shop lift data");
      return { success: false, result: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch shop lift:", error);
    toast.error(
      "Failed to fetch shop lift data"
    );
    return { success: false, result: null };
  }
};

// Brand Insights API
export const fetchBrandInsightsData = async (params: any) => {
  try {
    const url = endpoints.dashboard.brandInsights();
    const response = await axiosInstance.post<BrandInsightsResponse>(url, params);
    
    if (response?.data?.success && response?.data?.result) {
      return { success: true, result: response.data.result };
    } else {
      toast.error("Failed to fetch brand insights data");
      return { success: false, result: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch brand insights:", error);
    toast.error(
      "Failed to fetch brand insights data"
    );
    return { success: false, result: null };
  }
};

// Age Group Analysis API
export const fetchAgeGroupAnalysis = async (params: any) => {
  try {
    const response = await axiosInstance.get(endpoints.dashboard.ageGroupAnalysis(params));
    
    if (response?.data?.status === 'success') {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch age group analysis: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch age group analysis:", error);
    return { success: false, data: null };
  }
};

// Weekly Reach Analysis API
export const fetchWeeklyReachAnalysis = async (params: any) => {
  try {
    const response = await axiosInstance.post(endpoints.dashboard.weeklyReachAnalysis(), params);
    
    if (response?.data?.status === 'success') {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch weekly reach analysis: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch weekly reach analysis:", error);
    return { success: false, data: null };
  }
};

// Gender Analysis API
export const fetchGenderAnalysis = async (params: any) => {
  try {
    const response = await axiosInstance.get(endpoints.dashboard.genderAnalysis(params));
    
    if (response?.data?.status === 'success') {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch gender analysis: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch gender analysis:", error);
    return { success: false, data: null };
  }
};

// Contact Frequency Analysis API
export const fetchContactFrequencyAnalysis = async (params: any) => {
  try {
    const response = await axiosInstance.get(endpoints.dashboard.contactFrequencyAnalysis(params));
    
    if (response?.data?.status === 'success') {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch contact frequency analysis: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch contact frequency analysis:", error);
    return { success: false, data: null };
  }
};

// Media List API
export const fetchMediaList = async (
  clientId: string,
  campaignIds?: string | string[],
  startDate?: string,
  endDate?: string
) => {
  try {
    let url = endpoints.clients.mediaList(clientId);
    const params = new URLSearchParams();
    
    if (campaignIds) {
      if (Array.isArray(campaignIds)) {
        params.append('campaignIds', campaignIds.join(','));
      } else {
        params.append('campaignIds', campaignIds);
      }
    }
    
    if (startDate) {
      params.append('startDate', startDate);
    }
    
    if (endDate) {
      params.append('endDate', endDate);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await axiosInstance.get(url);
    
    if (response.data?.success && response.data?.result?.mediaList) {
      return { success: true, mediaList: response.data.result.mediaList };
    } else {
      return { success: false, mediaList: [] };
    }
  } catch (error: any) {
    console.error("Failed to fetch media list:", error);
    return { success: false, mediaList: [] };
  }
};

// Store Locations API
export const fetchStoreLocations = async (clientId: string): Promise<{ success: boolean; locations: StoreLocation[] }> => {
  try {
    const response = await axiosInstance.get(
      endpoints.campaigns.locationsByClient({ clientId })
    );
    
    if (response.data?.success && response.data?.result) {
      return { success: true, locations: response.data.result };
    } else {
      return { success: false, locations: [] };
    }
  } catch (error: any) {
    console.error("Failed to fetch store locations:", error);
    return { success: false, locations: [] };
  }
};

// Unified Weekly Reach Analysis API - Single endpoint for all dashboard data
export const fetchWeeklyReachAnalysisUnified = async (params: any): Promise<{ success: boolean; data: WeeklyReachAnalysisResponse | null }> => {
  try {
    const response = await axiosInstance.post<WeeklyReachAnalysisResponse>(endpoints.dashboard.weeklyReachAnalysis(), params);
    
    if (response?.data?.success && response?.data?.data) {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch weekly reach analysis: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch weekly reach analysis:", error);
    return { success: false, data: null };
  }
};

// Smart Request List API (Receipt List)
export const fetchSmartRequestList = async (clientId: string, startDate?: string, endDate?: string): Promise<{ success: boolean; data: SmartRequestListResponse | null }> => {
  try {
    let url = endpoints.clients.smartRequestList(clientId);
    const params: string[] = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    const response = await axiosInstance.get<SmartRequestListResponse>(url);
    
    if (response?.data?.success && response?.data?.result) {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch smart request list: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch smart request list:", error);
    return { success: false, data: null };
  }
};

// Visited Shop List API
export const fetchVisitedShopList = async (clientId: string, startDate?: string, endDate?: string): Promise<{ success: boolean; data: VisitedShopListResponse | null }> => {
  try {
    let url = endpoints.clients.visitedShopList(clientId);
    const params: string[] = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    const response = await axiosInstance.get<VisitedShopListResponse>(url);
    
    if (response?.data?.success && response?.data?.result) {
      return { success: true, data: response.data };
    } else {
      console.error('Failed to fetch visited shop list: Invalid response');
      return { success: false, data: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch visited shop list:", error);
    return { success: false, data: null };
  }
};

// Shop Effect API (Receipt Analysis)
export const fetchShopEffectData = async (params: any): Promise<{ success: boolean; result: ShopEffectResponse | null }> => {
  try {
    const url = endpoints.dashboard.shopEffect();
    const response = await axiosInstance.post<ShopEffectResponse>(url, params);
    
    if (response?.data?.success && response?.data?.data) {
      return { success: true, result: response.data };
    } else {
      toast.error("Failed to fetch shop effect data");
      return { success: false, result: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch shop effect:", error);
    toast.error(
      "Failed to fetch shop effect data"
    );
    return { success: false, result: null };
  }
};

// Receipt Analysis API
export const fetchReceiptAnalysisData = async (params: any): Promise<{ success: boolean; result: ReceiptAnalysisResponse | null }> => {
  try {
    const url = endpoints.dashboard.receiptAnalysis();
    const response = await axiosInstance.post<ReceiptAnalysisResponse>(url, params);
    
    if (response?.data?.success && response?.data?.result) {
      return { success: true, result: response.data };
    } else {
      toast.error("Failed to fetch receipt analysis data");
      return { success: false, result: null };
    }
  } catch (error: any) {
    console.error("Failed to fetch receipt analysis:", error);
    toast.error(
      "Failed to fetch receipt analysis data"
    );
    return { success: false, result: null };
  }
};
